import { init, track } from '@plausible-analytics/tracker';
import {
  definedProperties,
  type PlausibleProperties,
} from './plausibleProperties';
import {
  acquisitionSearch,
  pageviewUrl,
  sanitisePayload,
  type Pageview,
} from './plausibleUrl';

type PlausibleConfig = {
  /**
   * Site domain as registered in Plausible. Tracking stays off while this is
   * empty, which is how local builds opt out.
   */
  domain?: string;
};

let isInitialised = false;
let lastReportedPath: string | undefined;
let currentProperties: PlausibleProperties = {};

/** Replaces the custom properties attached to every event from now on. */
export const setPlausibleProperties = (properties: PlausibleProperties) => {
  currentProperties = properties;
};

export const initPlausible = (config: PlausibleConfig) => {
  const { domain } = config;

  if (!domain || isInitialised) return;

  init({
    domain,
    // Pageviews are reported against the matched route pattern instead, so a
    // visit to /projects/abc-123 aggregates under /projects/:projectId.
    autoCapturePageviews: false,
    // Keep `window.plausible` off the page: it sends location.href, which on
    // the customer-facing apps is a URL containing an access token.
    bindToWindow: false,
    transformRequest: sanitisePayload,
    customProperties: () => definedProperties(currentProperties),
  });

  isInitialised = true;
};

/**
 * Reports a pageview against the route's path pattern rather than the visited
 * path.
 *
 * The pageview must describe the location the route matched, not a fresh read
 * of `window.location`. A page can navigate between rendering and this call,
 * and pairing the new path with the old pattern misreports both pageviews.
 */
export const trackPageview = (pageview: Pageview) => {
  if (!isInitialised) return;

  // Keyed on the visited path plus the parameters that reach Plausible, so two
  // Projects each count while a filter or pagination change on one route does
  // not report the same URL twice.
  const path = pageview.pathname + acquisitionSearch(pageview.search);
  if (path === lastReportedPath) return;
  lastReportedPath = path;

  track('pageview', { url: pageviewUrl(window.location.origin, pageview) });
};
